import React, { useEffect, useState } from 'react';
import {
  CalculatorIcon,
  DeleteIcon,
  DragIcon,
  InformationIcon,
  CompendiumIcon,
  Pencil,
  ReferenceIcon,
} from 'src/images';
import styles from './lab-value.module.scss';
import classnames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS, Lang } from 'shared/constants';
import { DrugClassIcon } from 'src/components/drugClass/drugClassIcon/DrugClassIcon.component';
import { SingleTagIcon } from 'src/components/tags/tagsIcons';
import { Input } from 'antd';
import { MrkdwnEditor } from 'src/components/core';
import { IconButton } from 'src/components/design-system/buttons';
import { useSelector } from 'react-redux';
import { haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
const { TextArea } = Input;

const LabValueComponent = React.memo(function LabValueComponent({
  item = {
    title: '',
    value: '',
    ownerIds: [],
  },
  light = null,
  labSetItemDetails = () => {},
  adminMode = false,
  deleteValue = () => {},
  editValue = () => {},
  dragItem = () => {},
  findItem = () => {},
  dndGroup = '',
  draggable = true,
  finishDrag = () => {},
  showSpecialty = false,
  showContentType = false,
  updateLabItem,
  lang = Lang.LAB_VALUE_EDITOR,
  langTooltip = Lang.ACTION_BUTTON_TOOLTIPS,
}) {
  let { id, title, value, url, tags, drugClass, ownerIds, reference } = item;
  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));
  let boxRef = React.useRef(null);

  const [titleInput, setTitleInput] = useState(title);
  const [valueInput, setValueInput] = useState(value);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (titleInput !== title) {
      setTitleInput(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, setTitleInput]);

  useEffect(() => {
    if (valueInput !== value) {
      setValueInput(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, setValueInput]);

  const originalIndex = findItem(id)?.index;

  const [{ isDragging }, drag] = useDrag({
    item: { type: dndGroup, id, originalIndex },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => adminMode && draggable,
    end: (dropResult, monitor) => {
      const { id: droppedId, originalIndex } = monitor.getItem();
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        dragItem(droppedId, originalIndex);
      }

      finishDrag(droppedId);
    },
  });
  const [, drop] = useDrop({
    accept: dndGroup,
    canDrop: () => false,
    hover({ id: draggedId }) {
      if (draggedId !== id) {
        const { index: overIndex } = findItem(id);
        dragItem(draggedId, overIndex);
      }
    },
  });

  if (draggable) {
    boxRef = drag(drop(boxRef));
  }

  const onClick = () => {
    if (adminMode) {
      // TODO: analytics
      editValue({ item });
      return false;
    }
  };

  const openInformationInner = React.useCallback(
    e => {
      e.stopPropagation();

      analyticsService.track(EVENTS.LAB_ITEMS.OPEN_INFORMATION, {
        id: id.toString(),
        title,
        ownerIds,
        ...tags,
      });

      labSetItemDetails(id);
    },
    [id, labSetItemDetails, title, tags, ownerIds],
  );

  const openCalculator = React.useCallback(
    e => {
      e.stopPropagation();

      // TODO: analytics
      window.open(url);
    },
    [url],
  );

  const openReference = React.useCallback(
    e => {
      e.stopPropagation();

      // TODO: analytics
      window.open(reference);
    },
    [reference],
  );

  const deleteValueItem = React.useCallback(
    e => {
      e.stopPropagation();

      deleteValue({ id, title });
    },
    [deleteValue, id, title],
  );

  const onFocus = React.useCallback(() => {
    setDisabled(true);
  }, [setDisabled]);

  const onBlur = React.useCallback(
    async e => {
      const { value, name: field } = e.target;
      // TODO: add analytics

      if (value === item[field]) {
        setDisabled(false);
        return false;
      }
      await updateLabItem({ id, item: { ...item, [field]: value } });
      setDisabled(false);
    },
    [id, item, updateLabItem],
  );

  const onChange = React.useCallback(
    e => {
      if (e.target.name === 'title') {
        setTitleInput(e.target.value);
      }
      if (e.target.name === 'value') {
        setValueInput(e.target.value);
      }
    },
    [setTitleInput, setValueInput],
  );

  const renderActions = () => {
    return (
      <div className={styles.buttons}>
        {showSpecialty && tags.specialty && <SingleTagIcon tag={tags.specialty} />}
        {showContentType && tags.contentType && <SingleTagIcon tag={tags.contentType} />}
        {!!drugClass && <DrugClassIcon drugClass={drugClass} />}
        {!adminMode && (
          <>
            <IconButton
              onClick={openInformationInner}
              className={styles.buttonWrapper}
              label={langTooltip.INFO || 'View information'}
              showTooltip={true}
            >
              <InformationIcon className={styles.informationIcon} />
            </IconButton>

            {reference && (
              <IconButton
                label={langTooltip.OPEN_REFERENCE}
                onClick={openReference}
                className={styles.buttonWrapper}
              >
                <ReferenceIcon className={styles.referenceIcon} />
              </IconButton>
            )}

            {url && (
              <IconButton
                label={langTooltip.OPEN_CALCULATOR}
                onClick={openCalculator}
                className={styles.buttonWrapper}
              >
                <CalculatorIcon className={styles.informationIcon} />
              </IconButton>
            )}
          </>
        )}

        {adminMode && (
          <>
            <IconButton
              label={hasEditPermission ? langTooltip.DELETE : langTooltip.NOT_ALLOWED_TO_EDIT}
              onClick={deleteValueItem}
              className={styles.buttonWrapper}
              disabled={!hasEditPermission}
            >
              <DeleteIcon className={styles.deleteIcon} />
            </IconButton>
            <IconButton
              label={hasEditPermission ? langTooltip.EDIT : langTooltip.NOT_ALLOWED_TO_EDIT}
              onClick={onClick}
              className={styles.buttonWrapper}
              disabled={!hasEditPermission}
            >
              <Pencil className={styles.editIcon} />
            </IconButton>
            {draggable && (
              <IconButton
                label={lang.DRAG}
                className={classnames(styles.buttonWrapper, styles.dragIconWrapper)}
                /* Drag handle is hidden from keyboard navigation and screen readers
                since drag-and-drop is not accessible - users can reorder via other means */
                tabIndex={-1} //
                aria-hidden="true"
              >
                <DragIcon className={styles.dragIcon} />
              </IconButton>
            )}
          </>
        )}
      </div>
    );
  };

  const onChangeValue = async (name, value) => {
    setValueInput(value);

    if (value === item[name]) {
      setDisabled(false);
      return false;
    }
    await updateLabItem({ id, item: { ...item, [name]: value } });
    setDisabled(false);
  };

  const renderThumbnail = () => (
    <div className={styles.thumbnailWrapper}>
      <img src={CompendiumIcon} alt={titleInput} className={styles.itemIcon} />
    </div>
  );

  let opacity = isDragging ? 0 : 1;

  return (
    <li
      aria-label={titleInput}
      ref={boxRef}
      style={{ opacity }}
      className={classnames(styles.root, light ? styles.light : {})}
    >
      <div className={styles.content}>
        {renderThumbnail()}
        <div className={styles.textFields}>
          <TextArea
            onBlur={onBlur}
            onFocus={onFocus}
            className={styles.title}
            autoSize={{ minRows: 1 }}
            disabled={!adminMode || !hasEditPermission}
            value={titleInput}
            onChange={onChange}
            placeholder={lang.ITEM_TITLE_PLACEHOLDER}
            name={'title'}
          />
          {(adminMode || value) && (
            <MrkdwnEditor
              className={styles.valueWrapper}
              wrapperClass={styles.value}
              readOnly={!adminMode || !hasEditPermission}
              value={valueInput}
              onChange={onChangeValue}
              placeholder={lang.ITEM_VALUES_PLACEHOLDER}
              hideControllers={!disabled}
              name="value"
              inputStyle="inline"
              onFocus={onFocus}
            />
          )}
        </div>
      </div>

      {renderActions()}
    </li>
  );
});

export { LabValueComponent };
